import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Plus, Pencil } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SalesTargets() {
  const [targets, setTargets] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [formData, setFormData] = useState({
    staff_id: '',
    target_amount: 0,
    target_period: 'monthly',
    start_date: '',
    end_date: '',
    achieved_amount: 0
  });

  useEffect(() => {
    fetchTargets();
    fetchStaff();
  }, []);

  const fetchTargets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sales-targets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTargets(response.data);
    } catch (error) {
      toast.error('Failed to fetch targets');
    }
  };

  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/staff`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(response.data.filter(s => s.role.toLowerCase() === 'sales'));
    } catch (error) {
      console.error('Failed to fetch staff');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingTarget) {
        await axios.put(`${API}/sales-targets/${editingTarget.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Target updated successfully');
      } else {
        await axios.post(`${API}/sales-targets`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Target created successfully');
      }
      setOpen(false);
      resetForm();
      fetchTargets();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Operation failed';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const resetForm = () => {
    setFormData({
      staff_id: '',
      target_amount: 0,
      target_period: 'monthly',
      start_date: '',
      end_date: '',
      achieved_amount: 0
    });
    setEditingTarget(null);
  };

  const openEditDialog = (target) => {
    setEditingTarget(target);
    setFormData({
      staff_id: target.staff_id,
      target_amount: target.target_amount,
      target_period: target.target_period,
      start_date: target.start_date,
      end_date: target.end_date,
      achieved_amount: target.achieved_amount
    });
    setOpen(true);
  };

  const getStaffName = (staffId) => {
    const staffMember = staff.find(s => s.id === staffId);
    return staffMember ? staffMember.name : 'Unknown';
  };

  const calculateProgress = (achieved, target) => {
    return Math.min((achieved / target) * 100, 100);
  };

  return (
    <div data-testid="sales-targets-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Sales Targets</h1>
          <p className="text-slate-600 mt-1">Set and track sales performance</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-target-button" onClick={resetForm}>
              <Plus className="mr-2" size={16} />
              Set Target
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="target-dialog" className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingTarget ? 'Edit Target' : 'Set Sales Target'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="staff">Sales Staff *</Label>
                <Select value={formData.staff_id} onValueChange={(value) => setFormData({ ...formData, staff_id: value })}>
                  <SelectTrigger data-testid="staff-select">
                    <SelectValue placeholder="Select staff" />
                  </SelectTrigger>
                  <SelectContent>
                    {staff.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="target_amount">Target Amount *</Label>
                <Input
                  id="target_amount"
                  data-testid="target-amount-input"
                  type="number"
                  value={formData.target_amount}
                  onChange={(e) => setFormData({ ...formData, target_amount: parseFloat(e.target.value) })}
                  required
                  step="0.01"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="target_period">Target Period *</Label>
                <Select value={formData.target_period} onValueChange={(value) => setFormData({ ...formData, target_period: value })}>
                  <SelectTrigger data-testid="target-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date *</Label>
                  <Input
                    id="start_date"
                    data-testid="start-date-input"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date *</Label>
                  <Input
                    id="end_date"
                    data-testid="end-date-input"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="achieved_amount">Achieved Amount</Label>
                <Input
                  id="achieved_amount"
                  data-testid="achieved-amount-input"
                  type="number"
                  value={formData.achieved_amount}
                  onChange={(e) => setFormData({ ...formData, achieved_amount: parseFloat(e.target.value) })}
                  step="0.01"
                />
              </div>
              <Button data-testid="save-target-button" type="submit" className="w-full">
                {editingTarget ? 'Update Target' : 'Set Target'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Sales Staff</TableHead>
              <TableHead>Target Period</TableHead>
              <TableHead>Target Amount</TableHead>
              <TableHead>Achieved</TableHead>
              <TableHead>Progress</TableHead>
              <TableHead>Start Date</TableHead>
              <TableHead>End Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {targets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-slate-500">
                  No targets found. Set your first sales target.
                </TableCell>
              </TableRow>
            ) : (
              targets.map((target) => {
                const progress = calculateProgress(target.achieved_amount, target.target_amount);
                return (
                  <TableRow key={target.id} data-testid={`target-row-${target.id}`}>
                    <TableCell className="font-medium">{getStaffName(target.staff_id)}</TableCell>
                    <TableCell className="capitalize">{target.target_period}</TableCell>
                    <TableCell>Tsh {target.target_amount.toFixed(2)}</TableCell>
                    <TableCell>Tsh {target.achieved_amount.toFixed(2)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={progress} className="w-24" />
                        <span className="text-sm font-medium">{progress.toFixed(0)}%</span>
                      </div>
                    </TableCell>
                    <TableCell>{new Date(target.start_date).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(target.end_date).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        data-testid={`edit-target-${target.id}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(target)}
                      >
                        <Pencil size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
